import { useId, type CSSProperties } from 'react';
import { PERFORMANCE_BADGE_NAMES, performanceBadgeRank } from '../shared/performance-badge';
import './performance-score.css';

/** Presentation only. The benchmark owner must supply a validated comparable score. */
export function PerformanceScore({
  score,
  running = false,
  status,
  detail,
  onOpen,
}: {
  score: number | null;
  running?: boolean;
  status?: 'loading' | 'unavailable' | 'unrated';
  detail?: string;
  onOpen?: () => void;
}) {
  const id = useId().replaceAll(':', '');
  const measured =
    !status && score !== null && Number.isFinite(score) && score >= 1 && score <= 100;
  const rank = !measured || running ? null : performanceBadgeRank(score);
  const tier =
    rank === null
      ? 'untested'
      : rank < 2
        ? 'quiet'
        : rank < 6
          ? 'steady'
          : rank < 9
            ? 'strong'
            : 'powerful';
  const value = running
    ? 'Testing'
    : status === 'loading'
      ? 'Checking'
      : status === 'unavailable'
        ? 'No valid score'
        : status === 'unrated'
          ? 'Not rated'
          : measured
            ? Math.round(score!).toLocaleString()
            : 'Not tested';
  const label =
    measured && !running
      ? `Performance score ${Math.round(score!)}`
      : `Performance ${value.toLowerCase()}`;
  const Container = onOpen ? 'button' : 'div';
  return (
    <Container
      type={onOpen ? 'button' : undefined}
      role={onOpen ? undefined : 'group'}
      className={`performance-score ${tier} rank-${rank ?? 'none'}${running ? ' testing' : ''}`}
      aria-label={onOpen ? `${label}. Open speed checks` : label}
      onClick={onOpen}
      style={{ '--core-speed': `${rank === null ? 5 : 8 - rank * 0.58}s` } as CSSProperties}
    >
      <svg
        viewBox="0 0 150 110"
        aria-hidden="true"
        focusable="false"
        data-badge-rank={rank ?? 'unrated'}
      >
        <defs>
          <radialGradient id={`${id}-glow`}>
            <stop stopColor="currentColor" stopOpacity=".5" />
            <stop offset="1" stopColor="currentColor" stopOpacity="0" />
          </radialGradient>
          <linearGradient id={`${id}-core`} x1="0" y1="0" x2="1" y2="1">
            <stop stopColor="currentColor" stopOpacity=".85" />
            <stop offset="1" stopColor="currentColor" stopOpacity=".12" />
          </linearGradient>
        </defs>
        <ellipse className="core-aura" cx="75" cy="56" rx="64" ry="50" fill={`url(#${id}-glow)`} />
        {rank !== null && rank >= 4 && (
          <g className="core-wings" fill="currentColor">
            {Array.from({ length: rank - 3 }, (_, i) => (
              <g key={i} opacity={0.35 + i * 0.08}>
                <path
                  d={`M${37 - i * 3} ${36 + i * 7} ${19 - i * 2} ${28 + i * 7} ${26 - i * 2} ${43 + i * 7} ${38 - i * 2} ${48 + i * 5}Z`}
                />
                <path
                  d={`M${113 + i * 3} ${36 + i * 7} ${131 + i * 2} ${28 + i * 7} ${124 + i * 2} ${43 + i * 7} ${112 + i * 2} ${48 + i * 5}Z`}
                />
              </g>
            ))}
          </g>
        )}
        {rank !== null && rank >= 3 && (
          <g className="core-shell" fill="none" stroke="currentColor">
            <path opacity=".2" d="M44 20 75 4 106 20 126 54 106 88 75 104 44 88 24 54Z" />
            <path
              strokeWidth="2"
              opacity=".5"
              d="m48 24 27-14 27 14m16 27-16 32-27 15-27-15-16-32"
            />
          </g>
        )}
        {rank !== null && rank >= 6 && (
          <g className="core-wave" fill="none" stroke="currentColor">
            <ellipse cx="75" cy="55" rx="61" ry="24" strokeDasharray="60 12 5 12" />
            {rank >= 9 && <ellipse cx="75" cy="55" rx="48" ry="49" strokeDasharray="35 16" />}
          </g>
        )}
        {rank === 10 && (
          <g className="core-corona" fill="currentColor">
            {Array.from({ length: 12 }, (_, i) => (
              <path key={i} d="m73 4 2-9 2 9-2 5Z" transform={`rotate(${i * 30} 75 55)`} />
            ))}
          </g>
        )}
        <g className="core-heart">
          <path
            d={
              rank !== null && rank < 2
                ? 'M49 44Q75 53 101 44L99 76Q75 92 51 76Z'
                : 'm75 24 27 16v30L75 86 48 70V40Z'
            }
            fill={`url(#${id}-core)`}
            stroke="currentColor"
            strokeWidth={rank !== null && rank >= 8 ? 2.5 : 1.5}
          />
          {rank !== null && rank < 2 ? (
            <g fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <path
                d={rank === 0 ? 'm59 56 7 3m18 0 7-3M66 75q9-9 18 0' : 'M60 55v4m30-4v4M66 72h18'}
              />
              {rank === 1 && (
                <path d="m77 31-5 7h5l-2 7 8-10h-6Z" fill="currentColor" stroke="none" />
              )}
            </g>
          ) : (
            <>
              <path d="m75 34 17 10v20L75 74 58 64V44Z" fill="var(--cp-surface)" opacity=".75" />
              <path className="core-bolt" d="m79 38-15 20h10l-3 15 17-23H77Z" fill="currentColor" />
              {rank !== null && rank >= 8 && (
                <path d="m55 27 20-14 20 14-20-6Z" fill="currentColor" />
              )}
            </>
          )}
        </g>
        {rank !== null && rank >= 2 && (
          <g className="core-sparks" fill="currentColor">
            {Array.from({ length: rank }, (_, i) => (
              <circle
                key={i}
                cx={18 + ((i * 37) % 119)}
                cy={15 + ((i * 29) % 83)}
                r={rank >= 8 ? 1.8 : 1}
              />
            ))}
          </g>
        )}
      </svg>
      <span className="performance-score-copy">
        <small>Performance</small>
        <strong>{value}</strong>
        <span>
          {running
            ? 'Measuring this computer'
            : status === 'loading'
              ? 'Reading saved results'
              : detail
                ? detail
                : status === 'unavailable'
                  ? onOpen
                    ? 'Open speed checks'
                    : 'Run a new performance test'
                  : rank !== null
                    ? PERFORMANCE_BADGE_NAMES[rank]
                    : status === 'unrated'
                      ? 'Reference score unavailable'
                      : onOpen
                        ? 'Open speed checks'
                        : 'No test result yet'}
        </span>
      </span>
    </Container>
  );
}
